import JSEncrypt from 'jsencrypt';
import CryptoJS from 'crypto-js';

// ========== 配置项（与后端严格对齐） ==========
const RSA_PUBLIC_KEY = process.env.VUE_APP_RSAKEY

// AES配置（匹配后端：AES/CBC/PKCS5Padding）
// 修正：keySize单位是bit，ivSize单位是字节（统一为128bit=16字节）
const AES_CONFIG = {
  mode: CryptoJS.mode.CBC,
  padding: CryptoJS.pad.Pkcs7,
  keySize: 128, // 128bit = 16字节（AES-128）
  ivSize: 16    // 16字节（CBC模式必须）
};

// 后端配置对齐
const ENCRYPTED_DATA_FIELD = 'encryptedData';
const IV_FIELD = 'iv';
const ENCRYPTED_PARAM_SUFFIX = '_enc'; // 后端加密参数后缀

// ========== AES 方法（核心修复：Base64编码 + 长度修正） ==========
/**
 * 生成AES IV（16字节）→ Base64编码
 */
export function generateAesIv() {
  // 修正：生成16字节随机数组（AES_CONFIG.ivSize=16）
  const randomIv = CryptoJS.lib.WordArray.random(AES_CONFIG.ivSize);
  // 转换为Base64编码（核心修改：替换Hex为Base64）
  return randomIv.toString(CryptoJS.enc.Base64);
}

/**
 * 生成AES Key（16字节=128bit）→ Base64编码
 */
export function generateAesKey() {
  // 修正：生成16字节随机数组（AES_CONFIG.keySize/8=16）
  const randomKey = CryptoJS.lib.WordArray.random(AES_CONFIG.keySize / 8);
  // 转换为Base64编码（核心修改：替换Hex为Base64）
  return randomKey.toString(CryptoJS.enc.Base64);
}

/**
 * AES加密（数据加密后返回Base64，Key/IV为Base64格式）
 * @param {string} data 明文数据
 * @param {string} key Base64编码的AES Key
 * @param {string} iv Base64编码的AES IV
 * @returns {string} Base64编码的加密结果
 */
export function aesEncrypt(data, key, iv) {
  try {
    // 核心：Base64解码Key/IV为WordArray（原代码用Utf8解析是错误的）
    const keyWordArray = CryptoJS.enc.Base64.parse(key);
    const ivWordArray = CryptoJS.enc.Base64.parse(iv);
    
    const encrypted = CryptoJS.AES.encrypt(data, keyWordArray, {
      iv: ivWordArray,
      mode: AES_CONFIG.mode,
      padding: AES_CONFIG.padding,
      keySize: AES_CONFIG.keySize / 32 // 128/32=4（CryptoJS的keySize单位是32bit）
    });
    // 加密结果默认是Base64，直接返回
    return encrypted.toString();
  } catch (error) {
    console.error('AES加密失败：', error);
    throw new Error(`AES加密失败: ${error.message}`);
  }
}

/**
 * AES解密（加密数据为Base64，Key/IV为Base64格式）
 * @param {string} encryptedData Base64编码的加密数据
 * @param {string} key Base64编码的AES Key
 * @param {string} iv Base64编码的AES IV
 * @returns {string} 明文数据
 */
export function aesDecrypt(encryptedData, key, iv) {
  try {
    // 核心：Base64解码Key/IV为WordArray
    const keyWordArray = CryptoJS.enc.Base64.parse(key);
    const ivWordArray = CryptoJS.enc.Base64.parse(iv);
    
    const decrypted = CryptoJS.AES.decrypt(encryptedData, keyWordArray, {
      iv: ivWordArray,
      mode: AES_CONFIG.mode,
      padding: AES_CONFIG.padding,
      keySize: AES_CONFIG.keySize / 32
    });
    // 解密为Utf8明文
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('AES解密失败：', error);
    throw new Error(`AES解密失败: ${error.message}`);
  }
}

/**
 * RSA加密（明文→Base64编码结果，适配后端要求）
 * @param {string} data 明文（如Base64编码的AES Key）
 * @returns {string} Base64编码的RSA加密结果
 */
export const rsaEncrypt = (data) => {
  try {
    const encryptor = new JSEncrypt();
    encryptor.setPublicKey(RSA_PUBLIC_KEY);
    
    // JSEncrypt.encrypt默认返回Base64编码结果，无需额外处理
    const encrypted = encryptor.encrypt(data);
    
    if (!encrypted) {
      throw new Error('RSA加密返回空值，请检查：1.公钥格式是否为PEM；2.加密数据长度≤117字节');
    }
    return encrypted;
  } catch (error) {
    console.error('RSA加密失败：', error);
    throw new Error(`RSA加密失败: ${error.message}`);
  }
};

/**
 * 新增：RSA解密（可选，若前端需要解密后端RSA加密数据）
 * @param {string} encryptedData Base64编码的RSA加密数据
 * @returns {string} 明文（Base64编码的AES Key）
 */
export const rsaDecrypt = (encryptedData) => {
  try {
    const encryptor = new JSEncrypt();
    // 注意：解密需要私钥，前端一般不存储私钥，仅后端使用
    // encryptor.setPrivateKey(RSA_PRIVATE_KEY); 
    const decrypted = encryptor.decrypt(encryptedData);
    
    if (!decrypted) {
      throw new Error('RSA解密失败，请检查私钥或加密数据');
    }
    return decrypted;
  } catch (error) {
    console.error('RSA解密失败：', error);
    throw new Error(`RSA解密失败: ${error.message}`);
  }
};

// ========== URL参数加密方法（无结构变更，仅适配Base64的Key/IV） ==========
/**
 * 加密URL中的指定字段
 * @param {string} url 原始URL（如：/api/test?phone=13800138000&id=123）
 * @param {array} encryptFields 需要加密的字段名（如：['phone']）
 * @param {string} aesKey Base64编码的AES密钥
 * @param {string} iv Base64编码的AES初始向量
 * @returns {string} 加密后的URL（phone_enc=加密值&id=123）
 */
export function encryptUrlParams(url, encryptFields, aesKey, iv) {
  if (!url || !encryptFields || encryptFields.length === 0) {
    return url;
  }

  // 拆分URL：路径 + 参数
  const [urlPath, urlParamsStr] = url.split('?');
  if (!urlParamsStr) {
    return url; // 无参数直接返回
  }

  // 解析URL参数
  const params = new URLSearchParams(urlParamsStr);
  const encryptedParams = new URLSearchParams();

  // 遍历参数，加密指定字段（添加_enc后缀）
  for (const [key, value] of params.entries()) {
    if (encryptFields.includes(key)) {
      // 加密该字段，字段名添加_enc后缀（AES加密结果为Base64）
      const encryptedValue = aesEncrypt(value, aesKey, iv);
      encryptedParams.append(`${key}${ENCRYPTED_PARAM_SUFFIX}`, encryptedValue);
    } else {
      // 非加密字段直接保留
      encryptedParams.append(key, value);
    }
  }

  // 重组URL
  return `${urlPath}?${encryptedParams.toString()}`;
}

// ========== 业务封装方法（无结构变更，适配Base64） ==========
/**
 * 构建加密请求（支持URL参数+请求体加密）
 * @param {object} requestConfig 请求配置 { url, method, data, encryptUrlFields: [] }
 * @returns {object} 加密后的请求配置 + 加密参数
 */
export function buildEncryptedRequest(requestConfig) {
  const { url, method, data, encryptUrlFields = [] } = requestConfig;

  // 1. 生成Base64编码的AES Key和IV
  const aesKey = generateAesKey();
  const iv = generateAesIv();
  // 2. RSA加密Base64编码的AES Key（结果仍为Base64）
  const aesKeyEncrypted = rsaEncrypt(aesKey);

  // 3. 加密URL参数（如果有需要加密的字段）
  let encryptedUrl = url;
  if (encryptUrlFields.length > 0) {
    encryptedUrl = encryptUrlParams(url, encryptUrlFields, aesKey, iv);
  }

  // 4. 加密请求体（仅POST/PUT有body时）
  let encryptedBody = data;
  if (['post', 'put'].includes(method) && data) {
    const requestDataStr = JSON.stringify(data);
    encryptedBody = {
      [ENCRYPTED_DATA_FIELD]: aesEncrypt(requestDataStr, aesKey, iv), // Base64结果
      [IV_FIELD]: iv // Base64编码的IV
    };
  }

  return {
    // 加密后的请求配置
    requestConfig: {
      url: encryptedUrl,
      method,
      data: encryptedBody
    },
    // 加密参数（用于Header和响应解密）
    aesKeyEncrypted, // RSA加密后的AES Key（Base64）
    aesKey,          // 原始AES Key（Base64）
    iv               // 原始IV（Base64）
  };
}

/**
 * 解密响应数据（适配Base64的Key/IV）
 */
export function decryptResponse(responseData, aesKey) {
  if (!responseData || !responseData[ENCRYPTED_DATA_FIELD] || !responseData[IV_FIELD]) {
    return responseData;
  }

  try {
    const decryptedStr = aesDecrypt(
      responseData[ENCRYPTED_DATA_FIELD], // Base64加密数据
      aesKey,                             // Base64编码的AES Key
      responseData[IV_FIELD]              // Base64编码的IV
    );
    return JSON.parse(decryptedStr);
  } catch (error) {
    console.error('响应解密失败：', error);
    throw new Error(`响应解密失败: ${error.message}`);
  }
}